/**
 * SOLOMON — Shared file-attach routing
 *
 * Single source of truth for how dropped/selected files are classified and
 * routed. Used by BOTH the input bar (InputBar.jsx) and the full chat-area
 * drag-and-drop overlay (ChatInterface/index.jsx) so the image/doc/audio
 * split, the one-document-per-drop guard, and the accepted file-type rules
 * never diverge between the two surfaces.
 */

// ── Accepted audio/video (transcription path) ──
export const ACCEPTED_AUDIO_TYPES = new Set([
  'audio/mpeg',     // .mp3
  'audio/mp4',      // .m4a
  'audio/wav',
  'audio/x-wav',
  'audio/webm',
  'video/mp4',      // .mp4
  'video/webm',
  'audio/ogg',
  'audio/flac',
]);

export const ACCEPTED_AUDIO_EXTENSIONS = new Set([
  '.mp3', '.mp4', '.wav', '.m4a', '.webm', '.ogg', '.flac',
]);

// ── Accepted documents (RAG-attach path) ──
export const ACCEPTED_DOC_EXTENSIONS = new Set(['pdf', 'txt', 'md', 'markdown', 'docx', 'csv']);
export const ACCEPTED_DOC_MIMES = new Set([
  'application/pdf', 'text/plain', 'text/markdown', 'text/csv',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
]);

// ── Accepted images (vision path, ephemeral one-turn attachments) ──
export const ACCEPTED_IMAGE_EXTENSIONS = new Set(['png', 'jpg', 'jpeg', 'webp', 'gif']);
export const ACCEPTED_IMAGE_MIMES = new Set([
  'image/png', 'image/jpeg', 'image/webp', 'image/gif',
]);

export const MAX_AUDIO_FILE_SIZE = 500 * 1024 * 1024; // 500MB
export const MAX_DOC_FILE_SIZE   = 10 * 1024 * 1024;   //  10MB
export const MAX_IMAGE_FILE_SIZE = 20 * 1024 * 1024;   //  20MB source cap (downscaled before send)

export function isDocFile(file) {
  const ext = (file.name.split('.').pop() || '').toLowerCase();
  return ACCEPTED_DOC_EXTENSIONS.has(ext) || ACCEPTED_DOC_MIMES.has(file.type);
}

export function isImageFile(file) {
  const ext = (file.name.split('.').pop() || '').toLowerCase();
  return ACCEPTED_IMAGE_EXTENSIONS.has(ext) || ACCEPTED_IMAGE_MIMES.has(file.type);
}

function validateAudioFile(file) {
  if (!file) return 'No file selected.';
  const ext = '.' + file.name.split('.').pop().toLowerCase();
  if (!ACCEPTED_AUDIO_TYPES.has(file.type) && !ACCEPTED_AUDIO_EXTENSIONS.has(ext)) {
    return 'Unsupported file type. Accepted: MP3, MP4, WAV, M4A, WEBM, OGG, FLAC';
  }
  if (file.size > MAX_AUDIO_FILE_SIZE) {
    return `File too large (${(file.size / (1024 * 1024)).toFixed(0)} MB). Maximum is 500 MB.`;
  }
  return null;
}

/**
 * Route a single file to the correct handler (image vs doc vs audio), with
 * validation. Errors are surfaced via the onError callback rather than thrown.
 */
function routeFile(file, { onImageAttach, onDocumentAttach, onFileUpload, onError }) {
  // Image check runs first: an image MIME would otherwise fall through to the
  // audio validator and produce a misleading "unsupported type" error.
  if (isImageFile(file)) {
    if (!onImageAttach) {
      onError?.('Image attachments are not available from this surface.');
      return;
    }
    if (file.size > MAX_IMAGE_FILE_SIZE) {
      onError?.('Image too large (max 20 MB).');
      return;
    }
    onImageAttach(file);
    return;
  }
  if (isDocFile(file)) {
    if (file.size > MAX_DOC_FILE_SIZE) {
      onError?.('File too large (max 10 MB for documents).');
      return;
    }
    onDocumentAttach?.(file);
    return;
  }
  // audio/video path (transcription)
  const error = validateAudioFile(file);
  if (error) { onError?.(error); return; }
  onFileUpload?.(file);
}

/**
 * Split a FileList/array into images + docs + audio and route each.
 *
 * Documents must be attached one-at-a-time: each onDocumentAttach call closes
 * over the same server-side cumulative-budget snapshot, so a synchronous loop
 * can't enforce it. Take the first doc, warn (via onError) if there were more.
 * Audio files keep their existing batch behavior. Images batch — the 3-image
 * cap lives with the chip state in InputBar (the handler rejects over-cap),
 * not here, so both attach surfaces share one cap.
 *
 * @param {File[]|FileList} files
 * @param {{ onImageAttach?: Function, onDocumentAttach?: Function, onFileUpload?: Function, onError?: Function }} handlers
 */
export function attachFiles(files, { onImageAttach, onDocumentAttach, onFileUpload, onError } = {}) {
  const list = Array.from(files || []);
  if (list.length === 0) return;
  onError?.(null); // clear any prior error before routing

  const handlers = { onImageAttach, onDocumentAttach, onFileUpload, onError };

  const images = list.filter(isImageFile);
  const docs   = list.filter((f) => !isImageFile(f) && isDocFile(f));
  const audio  = list.filter((f) => !isImageFile(f) && !isDocFile(f));

  for (const file of images) routeFile(file, handlers);
  for (const file of audio) routeFile(file, handlers);

  if (docs.length > 0) {
    routeFile(docs[0], handlers);
    if (docs.length > 1) onError?.('Attach one document at a time.');
  }
}
